import { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Renderar children direkt i document.body (React Portal) och låser
 * bakgrundsscroll medan modalen är öppen.
 *
 * Portal:
 * - Undviker stacking context-fällor från föräldrar med transform/filter etc.
 *   (annars kan knappar hoppa upp ovanpå modalens innehåll — se iPad Safari-
 *   buggen där "Stämpla in" syntes över Hjälp-modalen).
 *
 * Body scroll lock:
 * - Utan detta scrollar iOS Safari bakgrunden när användaren swipar inuti
 *   modalen (scroll-propagering). Vi bevarar även scrollpositionen så
 *   användaren hamnar rätt när modalen stängs.
 */
export default function ModalPortal({ children }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const { body } = document;
    const scrollY = window.scrollY;
    // Spara originalvärden för att kunna återställa exakt
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    // Låst läge: fixed position bevarar scroll-position visuellt på iOS
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      // Återställ scroll-position
      window.scrollTo(0, scrollY);
    };
  }, []);

  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}
